import type {
  VisionOverrides,
  VisionSettings,
  VisionTransport,
} from "@/shared/domain/orgs";

/** The numeric vision settings, each typed as text in the form. */
export type VisionNumberKey =
  | "max_images"
  | "max_request_bytes"
  | "low_frames"
  | "standard_frames"
  | "high_frames";

/**
 * A catalog entry's vision overrides as the form holds them: `null` (or a
 * blank number) follows the family default.
 */
export interface VisionDraft extends Record<VisionNumberKey, string> {
  supports_images: boolean | null;
  transport: VisionTransport | null;
}

/** The numeric settings, in the order the form lists them. */
export const VISION_NUMBER_KEYS: readonly VisionNumberKey[] = [
  "max_images",
  "max_request_bytes",
  "low_frames",
  "standard_frames",
  "high_frames",
];

/** Display name of each numeric setting. */
export const VISION_NUMBER_LABELS: Record<VisionNumberKey, string> = {
  max_images: "Max images per request",
  max_request_bytes: "Max request bytes",
  low_frames: "Low frames",
  standard_frames: "Standard frames",
  high_frames: "High frames",
};

/** Display name of each transport. */
export const VISION_TRANSPORT_LABELS: Record<VisionTransport, string> = {
  converse: "Converse",
  openai_invoke: "OpenAI-compatible InvokeModel",
};

/** A vision draft from stored overrides; every key unset when there are none. */
export function visionDraftFromOverrides(
  overrides: VisionOverrides | undefined,
): VisionDraft {
  const text = (key: VisionNumberKey) => {
    const value = overrides?.[key];
    return value === null || value === undefined ? "" : String(value);
  };
  return {
    supports_images: overrides?.supports_images ?? null,
    transport: overrides?.transport ?? null,
    max_images: text("max_images"),
    max_request_bytes: text("max_request_bytes"),
    low_frames: text("low_frames"),
    standard_frames: text("standard_frames"),
    high_frames: text("high_frames"),
  };
}

/** A positive whole number, `null` when blank, or `NaN` when the text is not one. */
function wholeNumber(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  return /^[1-9]\d*$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}

/**
 * The overrides a draft sets: only the keys it sets, so `{}` follows the
 * family for everything. A number that is not a positive whole number
 * (which {@link visionDraftError} refuses) is left out.
 */
export function visionOverridesFromDraft(draft: VisionDraft): VisionOverrides {
  const overrides: VisionOverrides = {};
  if (draft.supports_images !== null) {
    overrides.supports_images = draft.supports_images;
  }
  if (draft.transport !== null) {
    overrides.transport = draft.transport;
  }
  for (const key of VISION_NUMBER_KEYS) {
    const value = wholeNumber(draft[key]);
    if (value !== null && !Number.isNaN(value)) {
      overrides[key] = value;
    }
  }
  return overrides;
}

/**
 * Why a vision draft cannot be saved, or `null` when it can. Every number
 * must be a positive whole number, and the effective frame budgets must run
 * low ≤ standard ≤ high. A blank budget counts as its family default
 * (`defaults`); while that is unknown (a new model), only the budgets set
 * are compared.
 */
export function visionDraftError(
  draft: VisionDraft,
  defaults: VisionSettings | undefined,
): string | null {
  for (const key of VISION_NUMBER_KEYS) {
    if (Number.isNaN(wholeNumber(draft[key]))) {
      return `${VISION_NUMBER_LABELS[key]} must be a positive whole number.`;
    }
  }
  const budgets = (["low_frames", "standard_frames", "high_frames"] as const)
    .map((key) => wholeNumber(draft[key]) ?? defaults?.[key] ?? null)
    .filter((value): value is number => value !== null);
  if (budgets.some((value, i) => i > 0 && value < budgets[i - 1])) {
    return "Frame budgets must run low ≤ standard ≤ high.";
  }
  return null;
}

/** Whether two sets of vision overrides set the same keys to the same values. */
export function sameVisionOverrides(
  a: VisionOverrides,
  b: VisionOverrides | undefined,
): boolean {
  const set = (overrides: VisionOverrides | undefined) =>
    Object.entries(overrides ?? {}).filter(([, value]) => value !== null);
  const left = set(a);
  const right = new Map(set(b));
  return (
    left.length === right.size &&
    left.every(([key, value]) => right.get(key) === value)
  );
}
